import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, RefreshCw, Download, CheckCircle2, AlertCircle, Users } from "lucide-react";

import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
}

function toCsvField(v) {
  if (v == null) return "";
  const s = String(v);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function AdminNewsletterManager() {
  const [loading, setLoading] = useState(true);
  const [resyncing, setResyncing] = useState(false);
  const [data, setData] = useState({ subscribers: [], count: 0, audience_id: null, audience_name: "" });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/newsletter/subscribers`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error("Impossible de charger les abonnés newsletter.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Initial load
    axios.get(`${API}/admin/newsletter/subscribers`, { withCredentials: true })
      .then((res) => setData(res.data))
      .catch(() => toast.error("Impossible de charger les abonnés newsletter."))
      .finally(() => setLoading(false));
  }, []);

  const handleResync = async () => {
    setResyncing(true);
    try {
      const res = await axios.post(`${API}/admin/newsletter/resync`, {}, { withCredentials: true });
      toast.success(`Synchro terminée : ${res.data.synced} contacts envoyés vers Brevo.`);
      await fetchData();
    } catch (err) {
      toast.error("Échec de la synchronisation Brevo.");
    } finally {
      setResyncing(false);
    }
  };

  const handleExportCsv = () => {
    const rows = [["Email", "Prénom", "Nom", "Date d'inscription", "Source", "Sync Brevo"]];
    data.subscribers.forEach((s) => {
      rows.push([
        s.email,
        s.first_name || "",
        s.last_name || "",
        formatDate(s.subscribed_at),
        s.source === "user" ? "Compte client" : "Inscription anonyme",
        s.brevo_synced ? "Oui" : "Non",
      ]);
    });
    const csv = rows.map((r) => r.map(toCsvField).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `newsletter-bilan2vie-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const syncedCount = data.subscribers.filter((s) => s.brevo_synced).length;
  const unsyncedCount = data.count - syncedCount;

  return (
    <div className="space-y-6" data-testid="admin-newsletter-section">
      {/* Header card with audience info + actions */}
      <Card className="bg-gradient-to-br from-[#3A115E]/40 via-[#261B3B]/60 to-[#1B132B]/40 border-[#D926A9]/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Mail className="w-5 h-5 text-[#D926A9]" />
            Newsletter — Liste Brevo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-[#1B132B]/60 rounded-xl p-4 border border-white/5">
              <div className="text-xs text-[#B59ED1] uppercase tracking-wider">Total abonnés</div>
              <div className="text-2xl font-serif text-white mt-1" data-testid="newsletter-total-count">{data.count}</div>
            </div>
            <div className="bg-[#1B132B]/60 rounded-xl p-4 border border-white/5">
              <div className="text-xs text-[#B59ED1] uppercase tracking-wider flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3 text-green-400" /> Synchro Brevo
              </div>
              <div className="text-2xl font-serif text-green-300 mt-1" data-testid="newsletter-synced-count">{syncedCount}</div>
            </div>
            <div className="bg-[#1B132B]/60 rounded-xl p-4 border border-white/5">
              <div className="text-xs text-[#B59ED1] uppercase tracking-wider flex items-center gap-1">
                <AlertCircle className="w-3 h-3 text-orange-400" /> En attente
              </div>
              <div className="text-2xl font-serif text-orange-300 mt-1">{unsyncedCount}</div>
            </div>
          </div>

          {data.audience_id ? (
            <div className="text-xs text-[#B59ED1] bg-[#1B132B]/40 rounded-lg p-3 border border-white/5">
              <div className="font-medium text-white mb-1">{data.audience_name}</div>
              <div className="font-mono break-all">ID : {data.audience_id}</div>
              <div className="mt-2">
                Connecte-toi sur <a href="https://app.brevo.com/contact/list-listing" target="_blank" rel="noreferrer" className="text-[#00E5FF] underline">app.brevo.com</a> puis crée un Broadcast ciblant cette audience pour envoyer ta campagne.
              </div>
            </div>
          ) : (
            <div className="text-xs text-orange-300 bg-orange-500/10 rounded-lg p-3 border border-orange-500/30">
              ⚠️ Aucune liste Brevo détectée. Lance une synchro pour la créer automatiquement.
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleResync}
              disabled={resyncing}
              className="bg-[#D926A9] hover:bg-[#D926A9]/90 text-white"
              data-testid="newsletter-resync-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${resyncing ? "animate-spin" : ""}`} />
              {resyncing ? "Synchro en cours…" : "Resynchroniser vers Brevo"}
            </Button>
            <Button
              onClick={handleExportCsv}
              variant="outline"
              className="border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10"
              data-testid="newsletter-export-csv-btn"
              disabled={!data.subscribers.length}
            >
              <Download className="w-4 h-4 mr-2" />
              Exporter en CSV
            </Button>
            <Button
              onClick={fetchData}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5"
              data-testid="newsletter-refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Rafraîchir
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Subscribers table */}
      <Card className="bg-[#261B3B] border-white/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Users className="w-5 h-5 text-[#00E5FF]" />
            Liste des abonnés ({data.count})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-[#B59ED1]">Chargement…</div>
          ) : data.subscribers.length === 0 ? (
            <div className="text-center py-12 text-[#B59ED1]">
              Aucun abonné pour le moment.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="newsletter-subscribers-table">
                <thead>
                  <tr className="text-left text-[#B59ED1] border-b border-white/10">
                    <th className="py-3 pr-3 font-medium">Email</th>
                    <th className="py-3 pr-3 font-medium">Prénom</th>
                    <th className="py-3 pr-3 font-medium">Nom</th>
                    <th className="py-3 pr-3 font-medium">Date</th>
                    <th className="py-3 pr-3 font-medium">Source</th>
                    <th className="py-3 pr-3 font-medium">Brevo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.subscribers.map((s) => (
                    <tr key={s.email} className="border-b border-white/5 hover:bg-white/5" data-testid={`newsletter-row-${s.email}`}>
                      <td className="py-3 pr-3 text-white font-mono text-xs">{s.email}</td>
                      <td className="py-3 pr-3 text-[#E2D5F8]">{s.first_name || "—"}</td>
                      <td className="py-3 pr-3 text-[#E2D5F8]">{s.last_name || "—"}</td>
                      <td className="py-3 pr-3 text-[#B59ED1] text-xs">{formatDate(s.subscribed_at)}</td>
                      <td className="py-3 pr-3">
                        <Badge variant="outline" className={s.source === "user" ? "border-[#9D4CDD]/40 text-[#9D4CDD]" : "border-[#00E5FF]/40 text-[#00E5FF]"}>
                          {s.source === "user" ? "Client" : "Anonyme"}
                        </Badge>
                      </td>
                      <td className="py-3 pr-3">
                        {s.brevo_synced ? (
                          <span className="inline-flex items-center gap-1 text-green-400 text-xs">
                            <CheckCircle2 className="w-3 h-3" /> OK
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-orange-400 text-xs">
                            <AlertCircle className="w-3 h-3" /> En attente
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
